import mustache from "mustache";
import { productoGrpcCatalogService } from "../../../../modulos/catalogos/aplicaciones/services/ProductoGrpcCatalogService";
import { ProductoItem } from "../../../../generated/grpc/catalogos_aplicaciones_pb";
import { TareaEjecucionDetalleProductoResponse } from "../../../../modulos/rastro/response/TareaEjecucionDetalleProductoResponse";
import { TareaEjecucionRequest } from "../../../../modulos/rastro/request/TareaEjecucionRequest";
import { IFormField } from "../../../definitions/form/IFormField";
import { AutocompleteRender } from "../../../implementations/form/autocomplete/AutocompleteRender";
import rowTemplate from "./tareaEjecucionProductoRowTemplate.html";
import template from "./tareaEjecucionProductosFieldTemplate.html";

mustache.parse(template);
mustache.parse(rowTemplate);

/** Contador para ids únicos por instancia. */
let TE_PRODUCTOS_ID = 0;

/** Campo de productos aplicados en una ejecución de tarea (`IFormField`):
 *  autocomplete del catálogo (sin duplicados) + tabla de filas con dosis
 *  editable, unidad del producto y quitar. Escribe `request.detalles[]` (solo
 *  `productoId`/`dosis`, como pide el server); el nombre/empresa/unidad para
 *  mostrar en las filas se resuelve aparte (no viajan en el request) — desde
 *  el detalle original (edición) y, al cargar, desde el catálogo completo. */
export class TareaEjecucionProductosField implements IFormField {
  /** Id único de la instancia (para los ids del DOM). */
  private readonly _fieldId: number;
  private readonly _request: TareaEjecucionRequest;
  /** Autocomplete del catálogo de productos. */
  private readonly _autocomplete: AutocompleteRender<ProductoItem>;
  /** Nombre/empresa/unidad por productoId, solo para render (no viaja al server). */
  private readonly _productosInfo = new Map<
    string,
    { nombre: string; empresa: string; unidad: string }
  >();

  /**
   * @param request           Request de la ejecución (se escribe `detalles`).
   * @param detallesIniciales Detalles del response original (edición): siembra
   *                          nombre/unidad antes de que cargue el catálogo.
   */
  constructor(
    request: TareaEjecucionRequest,
    detallesIniciales: TareaEjecucionDetalleProductoResponse[],
  ) {
    TE_PRODUCTOS_ID += 1;
    this._fieldId = TE_PRODUCTOS_ID;
    this._request = request;
    for (const detalle of detallesIniciales) {
      this._productosInfo.set(detalle.productoId, {
        nombre: detalle.productoDescripcion,
        empresa: "",
        unidad: detalle.unidadMedida,
      });
    }

    this._autocomplete = new AutocompleteRender<ProductoItem>({
      placeholder: "Productos",
      service: productoGrpcCatalogService,
      filter: (producto, texto) =>
        // Excluye los que ya están en la tabla (se reevalúa en cada apertura).
        !this._request.detalles.some(
          (detalle) => detalle.productoId === producto.id,
        ) &&
        (producto.descripcion.toLowerCase().includes(texto) ||
          producto.empresa.toLowerCase().includes(texto)),
      getOption: (producto) => ({
        title: producto.descripcion,
        subtitle: producto.empresa,
        badge: producto.unidadMedida,
      }),
      onPick: (producto) => this._onPick(producto),
    });
  }

  getId(): string {
    return `te-productos-${this._fieldId}`;
  }

  render(): string {
    return mustache.render(template, {
      id: this._fieldId,
      autocomplete: this._autocomplete.render(),
    });
  }

  connect(): void {
    this._autocomplete.connect();
    this._tabla = document.getElementById(`te-productos-${this._fieldId}`);
    this._rows = document.getElementById(`te-productos-rows-${this._fieldId}`);
    this._total = document.getElementById(`te-productos-total-${this._fieldId}`);
    this._error = document.getElementById(`te-productos-error-${this._fieldId}`);

    // Delegados: un listener por evento para todas las filas (sobreviven a los
    // re-render de la tabla).
    this._rows?.addEventListener("input", this._onDosisInput);
    this._rows?.addEventListener("click", this._onRemoveClick);

    // Catálogo completo (empresa + confirma nombre/unidad de las filas precargadas).
    void this._loadCatalogoAsync();
    this._refreshRows();
  }

  dispose(): void {
    this._disposed = true;
    this._rows?.removeEventListener("input", this._onDosisInput);
    this._rows?.removeEventListener("click", this._onRemoveClick);
    this._autocomplete.dispose();
  }

  /** Toda fila agregada exige dosis > 0 (la lista vacía es válida). */
  getError(): string | null {
    const invalida = this._request.detalles.some(
      (detalle) => detalle.dosis <= 0,
    );
    return invalida ? "Ingrese la dosis de cada producto" : null;
  }

  showError(): void {
    if (this._error) this._error.innerHTML = this.getError() ?? "";
  }

  focus(): void {
    // Con dosis inválida: foco al primer input de dosis en falta; si no, al
    // autocomplete.
    const index = this._request.detalles.findIndex(
      (detalle) => detalle.dosis <= 0,
    );
    if (index >= 0) {
      const input = this._rows?.querySelector<HTMLInputElement>(
        `[data-dosis="${index}"]`,
      );
      if (input) {
        input.focus();
        return;
      }
    }
    this._autocomplete.focus();
  }

  disabled(): void {
    this._autocomplete.disabled();
    this._setRowsDisabled(true);
  }

  enabled(): void {
    this._autocomplete.enabled();
    this._setRowsDisabled(false);
  }

  private _tabla: HTMLElement | null = null;
  private _rows: HTMLElement | null = null;
  private _total: HTMLElement | null = null;
  private _error: HTMLElement | null = null;
  private _disposed = false;

  /** Agrega el producto elegido como fila (ignora duplicados). */
  private _onPick(producto: ProductoItem): void {
    const yaAgregado = this._request.detalles.some(
      (detalle) => detalle.productoId === producto.id,
    );
    if (yaAgregado) return;

    this._productosInfo.set(producto.id, {
      nombre: producto.descripcion,
      empresa: producto.empresa,
      unidad: producto.unidadMedida,
    });
    this._request.detalles.push({ productoId: producto.id, dosis: 0 });
    this._refreshRows();
    this.showError();
  }

  /** Carga el catálogo completo (empresa + confirma nombre/unidad). */
  private async _loadCatalogoAsync(): Promise<void> {
    const productos = await productoGrpcCatalogService.getListAsync();
    if (this._disposed) return;
    productos.forEach((producto) => {
      this._productosInfo.set(producto.id, {
        nombre: producto.descripcion,
        empresa: producto.empresa,
        unidad: producto.unidadMedida,
      });
    });
    this._refreshRows();
  }

  /** Re-pinta la tabla desde `request.detalles` (fuente de verdad). */
  private _refreshRows(): void {
    if (!this._tabla || !this._rows || !this._total) return;

    const detalles = this._request.detalles;
    this._tabla.style.display = detalles.length > 0 ? "" : "none";
    this._total.textContent = String(detalles.length);
    this._rows.innerHTML = detalles
      .map((detalle, index) => {
        const info = this._productosInfo.get(detalle.productoId);
        return mustache.render(rowTemplate, {
          index: index,
          nombre: info?.nombre ?? "",
          empresa: info?.empresa ?? "",
          dosis: detalle.dosis > 0 ? detalle.dosis : "",
          unidad: info?.unidad ?? "",
        });
      })
      .join("");
  }

  /** Delegado: escribe la dosis editada en su detalle. */
  private _onDosisInput = (e: Event): void => {
    const input = e.target as HTMLInputElement | null;
    const index = Number(input?.dataset.dosis ?? "");
    if (!input || isNaN(index) || !this._request.detalles[index]) return;
    const dosis = parseFloat(input.value);
    this._request.detalles[index].dosis = isNaN(dosis) ? 0 : dosis;
    this.showError();
  };

  /** Delegado: quita la fila del detalle. */
  private _onRemoveClick = (e: Event): void => {
    const btn = (e.target as HTMLElement | null)?.closest<HTMLElement>(
      "[data-remove]",
    );
    if (!btn) return;
    const index = Number(btn.dataset.remove);
    if (isNaN(index)) return;
    this._request.detalles.splice(index, 1);
    this._refreshRows();
    this.showError();
  };

  /** Habilita/deshabilita los controles de las filas. */
  private _setRowsDisabled(disabled: boolean): void {
    this._rows
      ?.querySelectorAll<HTMLInputElement | HTMLButtonElement>("input, button")
      .forEach((el) => {
        el.disabled = disabled;
      });
  }
}
